import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DashboardIcon from '@mui/icons-material/Dashboard';
import InboxIcon from '@mui/icons-material/Inbox';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import StarIcon from '@mui/icons-material/Star';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import GroupsIcon from '@mui/icons-material/Groups';
import PeopleIcon from '@mui/icons-material/People';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import CategoryIcon from '@mui/icons-material/Category';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import { useAuth } from '../context/AuthContext';
import { workerAPI, contactAPI, adminAPI, reviewAPI, catalogAPI } from '../api/endpoints';
import StarRating from '../components/StarRating';
import Lightbox from '../components/Lightbox';
import './AdminPanel.css';

const AdminPanel = () => {
  const { user } = useAuth();
  const [worker, setWorker] = useState(null);
  const [requests, setRequests] = useState([]);
  const [tab, setTab] = useState('overview');
  
  // Admin/Supervisor data
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  
  // Filtros
  const [searchClient, setSearchClient] = useState('');
  const [searchWorker, setSearchWorker] = useState('');
  const [searchProject, setSearchProject] = useState('');
  const [searchUser, setSearchUser] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterVerified, setFilterVerified] = useState('all');
  const [loading, setLoading] = useState(false);
  const [galleryImages, setGalleryImages] = useState(null);

  // Evaluación de supervisor (modal)
  const [evalWorker, setEvalWorker] = useState(null);
  const [evalForm, setEvalForm] = useState(null);
  const [evalSaving, setEvalSaving] = useState(false);
  const [evalError, setEvalError] = useState('');

  // Crear usuario (modal, solo admin)
  const [newUser, setNewUser] = useState(null);
  const [newUserSaving, setNewUserSaving] = useState(false);
  const [newUserError, setNewUserError] = useState('');

  // Categorías (solo admin)
  const [categories, setCategories] = useState([]);
  const [catForm, setCatForm] = useState({ name: '', icon: '', description: '' });
  const [catSaving, setCatSaving] = useState(false);
  const [catError, setCatError] = useState('');
  
  // Determinar si es admin/supervisor
  const isAdmin = user?.role === 'admin' || user?.role === 'supervisor';
  // Solo el administrador puede gestionar usuarios (cambiar rol, estado, eliminar).
  const isSuperAdmin = user?.role === 'admin';

  // Cargar datos del trabajador
  useEffect(() => {
    workerAPI.me().then((r) => setWorker(r.data)).catch(() => setWorker(false));
    contactAPI.list().then((r) => setRequests(r.data.results ?? r.data)).catch(() => {});
  }, []);

  // Cargar datos de admin
  useEffect(() => {
    if (isAdmin && (tab === 'overview' || tab === 'clients' || tab === 'workers' || tab === 'projects' || tab === 'users' || tab === 'categories')) {
      loadAdminData();
    }
  }, [isAdmin, tab, searchClient, searchWorker, searchProject, searchUser, filterRole, filterVerified]);

  const loadAdminData = async () => {
    setLoading(true);
    try {
      // Cargar estadísticas
      if (tab === 'overview') {
        const res = await adminAPI.stats();
        setStats(res.data);
      }
      
      // Cargar clientes
      if (tab === 'clients') {
        const res = await adminAPI.clientsList({ search: searchClient, page_size: 50 });
        setClients(res.data.results || []);
      }
      
      // Cargar trabajadores
      if (tab === 'workers') {
        const params = { page_size: 50 };
        if (searchWorker) params.search = searchWorker;
        if (filterVerified !== 'all') params.verified = filterVerified === 'verified';
        
        const res = await adminAPI.workersList(params);
        setWorkers(res.data.results || []);
      }
      
      // Cargar proyectos
      if (tab === 'projects') {
        const res = await adminAPI.projectsReport({ search: searchProject, page_size: 50 });
        setProjects(res.data.results || []);
      }
      
      // Cargar usuarios (gestión)
      if (tab === 'users') {
        const params = { page_size: 100 };
        if (searchUser) params.search = searchUser;
        if (filterRole !== 'all') params.role = filterRole;
        const res = await adminAPI.usersList(params);
        setUsers(res.data.results || []);
      }

      // Cargar categorías
      if (tab === 'categories') {
        const res = await catalogAPI.categories();
        setCategories(res.data.results ?? res.data);
      }
    } catch (error) {
      console.error('Error loading admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const changeUserRole = async (id, role) => {
    try {
      const res = await adminAPI.updateUser(id, { role });
      setUsers((us) => us.map((u) => (u.id === id ? res.data : u)));
    } catch (error) {
      alert(error.response?.data?.detail || 'No se pudo cambiar el rol.');
    }
  };

  const toggleUserActive = async (id, isActive) => {
    try {
      const res = await adminAPI.updateUser(id, { is_active: isActive });
      setUsers((us) => us.map((u) => (u.id === id ? res.data : u)));
    } catch (error) {
      alert(error.response?.data?.detail || 'No se pudo cambiar el estado.');
    }
  };

  const removeUser = async (id, name) => {
    if (!window.confirm(`¿Eliminar al usuario "${name}"? Esta acción no se puede deshacer.`)) return;
    try {
      await adminAPI.deleteUser(id);
      setUsers((us) => us.filter((u) => u.id !== id));
    } catch (error) {
      alert(error.response?.data?.detail || 'No se pudo eliminar el usuario.');
    }
  };

  const changeStatus = (id, status) => {
    contactAPI.updateStatus(id, status).then(() => {
      setRequests((rs) => rs.map((r) => (r.id === id ? { ...r, status } : r)));
    });
  };

  const EVAL_FIELDS = [
    ['punctuality', 'Puntualidad'],
    ['work_quality', 'Calidad del trabajo'],
    ['cleanliness', 'Limpieza'],
    ['safety', 'Seguridad'],
    ['customer_treatment', 'Trato al cliente'],
  ];

  const openCreateUser = () => {
    setNewUserError('');
    setNewUser({
      username: '', password: '', first_name: '', last_name: '',
      email: '', phone: '', role: 'supervisor',
    });
  };

  const submitNewUser = async () => {
    setNewUserSaving(true);
    setNewUserError('');
    try {
      await adminAPI.createUser(newUser);
      setNewUser(null);
      if (tab === 'users') loadAdminData();
    } catch (error) {
      setNewUserError(error.response?.data?.detail || 'No se pudo crear el usuario.');
    } finally {
      setNewUserSaving(false);
    }
  };

  const addCategory = async (e) => {
    e.preventDefault();
    setCatSaving(true);
    setCatError('');
    try {
      await catalogAPI.createCategory(catForm);
      setCatForm({ name: '', icon: '', description: '' });
      loadAdminData();
    } catch (error) {
      const data = error.response?.data;
      setCatError(
        data?.detail
        || data?.name?.[0]
        || 'No se pudo crear la categoría.'
      );
    } finally {
      setCatSaving(false);
    }
  };

  const removeCategory = async (cat) => {
    const warning = (cat.workers_count || cat.services_count)
      ? ` Tiene ${cat.services_count} servicio(s) y ${cat.workers_count} trabajador(es) asociados; los servicios se eliminarán y los trabajadores quedarán sin categoría.`
      : '';
    if (!window.confirm(`¿Eliminar la categoría "${cat.name}"?${warning}`)) return;
    try {
      await catalogAPI.deleteCategory(cat.id);
      setCategories((cs) => cs.filter((c) => c.id !== cat.id));
    } catch (error) {
      alert(error.response?.data?.detail || 'No se pudo eliminar la categoría.');
    }
  };

  const openEval = async (w) => {
    setEvalWorker(w);
    setEvalError('');
    setEvalForm(null);
    try {
      // Busca una evaluación existente para prellenar (es una por trabajador).
      const res = await reviewAPI.supervisorList({ worker: w.id });
      const existing = (res.data.results ?? res.data)[0];
      setEvalForm({
        id: existing?.id ?? null,
        punctuality: existing?.punctuality ?? 5,
        work_quality: existing?.work_quality ?? 5,
        cleanliness: existing?.cleanliness ?? 5,
        safety: existing?.safety ?? 5,
        customer_treatment: existing?.customer_treatment ?? 5,
        comment: existing?.comment ?? '',
      });
    } catch (error) {
      setEvalForm({
        id: null, punctuality: 5, work_quality: 5, cleanliness: 5,
        safety: 5, customer_treatment: 5, comment: '',
      });
    }
  };

  const submitEval = async () => {
    setEvalSaving(true);
    setEvalError('');
    const payload = {
      worker: evalWorker.id,
      punctuality: Number(evalForm.punctuality),
      work_quality: Number(evalForm.work_quality),
      cleanliness: Number(evalForm.cleanliness),
      safety: Number(evalForm.safety),
      customer_treatment: Number(evalForm.customer_treatment),
      comment: evalForm.comment,
    };
    try {
      if (evalForm.id) {
        await reviewAPI.supervisorUpdate(evalForm.id, payload);
      } else {
        await reviewAPI.supervisorCreate(payload);
      }
      setEvalWorker(null);
      setEvalForm(null);
      if (tab === 'workers') loadAdminData();
    } catch (error) {
      setEvalError(error.response?.data?.detail || 'No se pudo guardar la evaluación.');
    } finally {
      setEvalSaving(false);
    }
  };

  return (
    <div className="admin-page">
      <div className="container admin-layout">
        <aside className="admin-side">
          <div className="admin-user">
            <div className="admin-avatar">
              {(user?.first_name || user?.username || 'U').charAt(0).toUpperCase()}
            </div>
            <div>
              <strong>{user?.full_name || user?.username}</strong>
              <span>{user?.role}</span>
            </div>
          </div>
          <nav className="admin-nav">
            {/* Tabs generales */}
            <button className={tab === 'overview' ? 'active' : ''} onClick={() => setTab('overview')}>
              <DashboardIcon sx={{ fontSize: 20 }} /> Resumen
            </button>
            <button className={tab === 'requests' ? 'active' : ''} onClick={() => setTab('requests')}>
              <InboxIcon sx={{ fontSize: 20 }} /> Solicitudes
            </button>
            
            {/* Tabs de admin/supervisor */}
            {isAdmin && (
              <>
                <button className={tab === 'clients' ? 'active' : ''} onClick={() => setTab('clients')}>
                  <GroupsIcon sx={{ fontSize: 20 }} /> Clientes
                </button>
                <button className={tab === 'workers' ? 'active' : ''} onClick={() => setTab('workers')}>
                  <PeopleIcon sx={{ fontSize: 20 }} /> Trabajadores
                </button>
                <button className={tab === 'projects' ? 'active' : ''} onClick={() => setTab('projects')}>
                  <PhotoLibraryIcon sx={{ fontSize: 20 }} /> Proyectos
                </button>
                {isSuperAdmin && (
                  <button className={tab === 'users' ? 'active' : ''} onClick={() => setTab('users')}>
                    <ManageAccountsIcon sx={{ fontSize: 20 }} /> Usuarios
                  </button>
                )}
                {isSuperAdmin && (
                  <button className={tab === 'categories' ? 'active' : ''} onClick={() => setTab('categories')}>
                    <CategoryIcon sx={{ fontSize: 20 }} /> Categorías
                  </button>
                )}
              </>
            )}
            
            <Link to="/subir-proyecto" className="admin-nav-link">
              <AddCircleIcon sx={{ fontSize: 20 }} /> Subir proyecto
            </Link>
            <Link to="/mi-perfil" className="admin-nav-link">
              <DashboardIcon sx={{ fontSize: 20 }} /> Editar perfil
            </Link>
          </nav>
        </aside>

        <section className="admin-content">
          {/* ===== RESUMEN GENERAL ===== */}
          {tab === 'overview' && (
            <>
              <h1>Resumen</h1>
              
              {/* Si es admin/supervisor, mostrar estadísticas generales */}
              {isAdmin && stats && (
                <>
                  <div className="admin-cards">
                    <div className="admin-card">
                      <GroupsIcon sx={{ fontSize: 28, color: '#0e2433' }} />
                      <strong>{stats.total_clients}</strong>
                      <span>Clientes</span>
                    </div>
                    <div className="admin-card">
                      <PeopleIcon sx={{ fontSize: 28, color: '#1e9e6a' }} />
                      <strong>{stats.total_workers}</strong>
                      <span>Trabajadores</span>
                    </div>
                    <div className="admin-card">
                      <StarIcon sx={{ fontSize: 28, color: '#ff6a00' }} />
                      <strong>{stats.avg_worker_rating}</strong>
                      <span>Rating promedio</span>
                    </div>
                    <div className="admin-card">
                      <PhotoLibraryIcon sx={{ fontSize: 28, color: '#ff6b6b' }} />
                      <strong>{stats.total_projects}</strong>
                      <span>Proyectos</span>
                    </div>
                    <div className="admin-card">
                      <InboxIcon sx={{ fontSize: 28, color: '#4dabf7' }} />
                      <strong>{stats.total_contacts}</strong>
                      <span>Solicitudes</span>
                    </div>
                    <div className="admin-card">
                      <StarIcon sx={{ fontSize: 28, color: '#845ef7' }} />
                      <strong>{stats.total_reviews}</strong>
                      <span>Reseñas</span>
                    </div>
                  </div>
                  <div className="admin-card-info">
                    <h3>Información del Sistema</h3>
                    <p><strong>Total de usuarios:</strong> {stats.total_users}</p>
                    <p><strong>Trabajadores verificados:</strong> {stats.verified_workers} / {stats.total_workers}</p>
                  </div>
                </>
              )}
              
              {/* Si es trabajador, mostrar su perfil */}
              {!isAdmin && worker ? (
                <>
                  <div className="admin-cards">
                    <div className="admin-card">
                      <PhotoLibraryIcon sx={{ fontSize: 28, color: '#0e2433' }} />
                      <strong>{worker.projects_count}</strong>
                      <span>Proyectos</span>
                    </div>
                    <div className="admin-card">
                      <StarIcon sx={{ fontSize: 28, color: '#ff6a00' }} />
                      <strong>{worker.average_rating}</strong>
                      <span>Calificación</span>
                    </div>
                    <div className="admin-card">
                      <InboxIcon sx={{ fontSize: 28, color: '#1e9e6a' }} />
                      <strong>{requests.length}</strong>
                      <span>Solicitudes</span>
                    </div>
                  </div>
                  <div className="admin-profile-card">
                    <h3>{worker.name}</h3>
                    <p>{worker.headline}</p>
                    <StarRating value={worker.average_rating} showValue count={worker.reviews_count} />
                    <Link to={`/maestros/${worker.id}`} className="btn btn-outline" style={{ marginTop: 16 }}>
                      Ver mi perfil público
                    </Link>
                  </div>
                </>
              ) : (
                !isAdmin && <div className="admin-empty">
                  <p>Aún no tienes un perfil de trabajador configurado.</p>
                  <Link to="/mi-perfil" className="btn btn-primary">Crear perfil</Link>
                </div>
              )}
            </>
          )}

          {/* ===== SOLICITUDES DE CONTACTO ===== */}
          {tab === 'requests' && (
            <>
              <h1>Solicitudes de contacto / cotización</h1>
              {requests.length === 0 ? (
                <div className="admin-empty"><p>No tienes solicitudes todavía.</p></div>
              ) : (
                <div className="admin-table">
                  <div className="admin-table-head">
                    <span>Cliente</span>
                    <span>Tipo</span>
                    <span>Teléfono</span>
                    <span>Zona</span>
                    <span>Estado</span>
                  </div>
                  {requests.map((r) => (
                    <div key={r.id} className="admin-table-row">
                      <span data-label="Cliente"><strong>{r.client_name}</strong><br /><small>{r.message}</small></span>
                      <span data-label="Tipo">{r.request_type_display}</span>
                      <span data-label="Teléfono">{r.client_phone}</span>
                      <span data-label="Zona">{r.zone || '—'}</span>
                      <span data-label="Estado">
                        <select className={`status status-${r.status}`} value={r.status}
                          onChange={(e) => changeStatus(r.id, e.target.value)}>
                          <option value="new">Nueva</option>
                          <option value="in_progress">En proceso</option>
                          <option value="closed">Cerrada</option>
                        </select>
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* ===== LISTA DE CLIENTES ===== */}
          {tab === 'clients' && isAdmin && (
            <>
              <h1>Lista de Clientes</h1>
              <div className="admin-search">
                <SearchIcon sx={{ fontSize: 20 }} />
                <input 
                  type="text" 
                  placeholder="Buscar por nombre, email..."
                  value={searchClient}
                  onChange={(e) => setSearchClient(e.target.value)}
                />
              </div>
              {loading ? (
                <div className="admin-loading"><p>Cargando...</p></div>
              ) : clients.length === 0 ? (
                <div className="admin-empty"><p>No hay clientes registrados.</p></div>
              ) : (
                <div className="admin-table">
                  <div className="admin-table-head">
                    <span>Nombre</span>
                    <span>Email</span>
                    <span>Teléfono</span>
                    <span>Fecha de registro</span>
                  </div>
                  {clients.map((c) => (
                    <div key={c.id} className="admin-table-row">
                      <span data-label="Nombre"><strong>{c.full_name}</strong></span>
                      <span data-label="Email">{c.email}</span>
                      <span data-label="Teléfono">{c.phone || '—'}</span>
                      <span data-label="Fecha">{new Date(c.date_joined).toLocaleDateString()}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* ===== LISTA DE TRABAJADORES ===== */}
          {tab === 'workers' && isAdmin && (
            <>
              <h1>Lista de Trabajadores</h1>
              <div className="admin-filters">
                <div className="admin-search">
                  <SearchIcon sx={{ fontSize: 20 }} />
                  <input 
                    type="text" 
                    placeholder="Buscar por nombre, email..."
                    value={searchWorker}
                    onChange={(e) => setSearchWorker(e.target.value)}
                  />
                </div>
                <select 
                  value={filterVerified} 
                  onChange={(e) => setFilterVerified(e.target.value)}
                  className="admin-select"
                >
                  <option value="all">Todos</option>
                  <option value="verified">Verificados</option>
                  <option value="unverified">No verificados</option>
                </select>
              </div>
              {loading ? (
                <div className="admin-loading"><p>Cargando...</p></div>
              ) : workers.length === 0 ? (
                <div className="admin-empty"><p>No hay trabajadores registrados.</p></div>
              ) : (
                <div className="admin-table">
                  <div className="admin-table-head">
                    <span>Nombre</span>
                    <span>Email</span>
                    <span>Rating</span>
                    <span>Proyectos</span>
                    <span>Verificado</span>
                    <span>Fecha de registro</span>
                    <span>Evaluación</span>
                  </div>
                  {workers.map((w) => (
                    <div key={w.id} className="admin-table-row">
                      <span data-label="Nombre"><strong>{w.name}</strong></span>
                      <span data-label="Email">{w.user?.email || '—'}</span>
                      <span data-label="Rating">
                        <StarRating value={w.average_rating} size="small" />
                      </span>
                      <span data-label="Proyectos">{w.projects_count}</span>
                      <span data-label="Verificado">
                        <span className={`badge ${w.is_verified ? 'badge-success' : 'badge-warning'}`}>
                          {w.is_verified ? '✓ Sí' : '✗ No'}
                        </span>
                      </span>
                      <span data-label="Fecha">{new Date(w.created_at).toLocaleDateString()}</span>
                      <span data-label="Evaluación">
                        <button className="btn btn-outline btn-sm" onClick={() => openEval(w)}>
                          Evaluar
                        </button>
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* ===== REPORTE DE PROYECTOS ===== */}
          {tab === 'projects' && isAdmin && (
            <>
              <h1>Reporte de Proyectos</h1>
              <div className="admin-search">
                <SearchIcon sx={{ fontSize: 20 }} />
                <input 
                  type="text" 
                  placeholder="Buscar por título, descripción, ubicación..."
                  value={searchProject}
                  onChange={(e) => setSearchProject(e.target.value)}
                />
              </div>
              {loading ? (
                <div className="admin-loading"><p>Cargando...</p></div>
              ) : projects.length === 0 ? (
                <div className="admin-empty"><p>No hay proyectos registrados.</p></div>
              ) : (
                <div className="admin-table">
                  <div className="admin-table-head">
                    <span>Título</span>
                    <span>Trabajador</span>
                    <span>Servicio</span>
                    <span>Ubicación</span>
                    <span>Fecha de trabajo</span>
                    <span>Fotos</span>
                  </div>
                  {projects.map((p) => {
                    const photos = (p.photos || []).map((ph) => ph.image);
                    return (
                      <div key={p.id} className="admin-table-row">
                        <span data-label="Título"><strong>{p.title}</strong></span>
                        <span data-label="Trabajador">{p.worker_name || '—'}</span>
                        <span data-label="Servicio">{p.service_name || '—'}</span>
                        <span data-label="Ubicación">{p.location || '—'}</span>
                        <span data-label="Fecha">{p.work_date ? new Date(p.work_date).toLocaleDateString() : '—'}</span>
                        <span data-label="Fotos">
                          {photos.length > 0 ? (
                            <button
                              type="button"
                              className="btn btn-outline btn-sm"
                              onClick={() => setGalleryImages(photos)}
                            >
                              Ver {photos.length}
                            </button>
                          ) : (
                            '0'
                          )}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {/* ===== GESTIÓN DE USUARIOS ===== */}
          {tab === 'users' && isSuperAdmin && (
            <>
              <div className="admin-head-row">
                <h1>Gestión de Usuarios</h1>
                <button className="btn btn-primary" onClick={openCreateUser}>
                  + Crear usuario
                </button>
              </div>
              <div className="admin-filters">
                <div className="admin-search">
                  <SearchIcon sx={{ fontSize: 20 }} />
                  <input
                    type="text"
                    placeholder="Buscar por nombre, email, usuario..."
                    value={searchUser}
                    onChange={(e) => setSearchUser(e.target.value)}
                  />
                </div>
                <select
                  value={filterRole}
                  onChange={(e) => setFilterRole(e.target.value)}
                  className="admin-select"
                >
                  <option value="all">Todos los roles</option>
                  <option value="client">Clientes</option>
                  <option value="worker">Trabajadores</option>
                  <option value="supervisor">Supervisores</option>
                  <option value="admin">Administradores</option>
                </select>
              </div>
              {loading ? (
                <div className="admin-loading"><p>Cargando...</p></div>
              ) : users.length === 0 ? (
                <div className="admin-empty"><p>No hay usuarios que coincidan.</p></div>
              ) : (
                <div className="admin-table">
                  <div className="admin-table-head">
                    <span>Nombre</span>
                    <span>Email</span>
                    <span>Rol</span>
                    <span>Estado</span>
                    <span>Acciones</span>
                  </div>
                  {users.map((u) => (
                    <div key={u.id} className="admin-table-row">
                      <span data-label="Nombre">
                        <strong>{u.full_name || u.username}</strong><br />
                        <small>@{u.username}</small>
                      </span>
                      <span data-label="Email">{u.email || '—'}</span>
                      <span data-label="Rol">
                        {u.id === user?.id ? (
                          <span className="badge badge-success">{u.role_display} (tú)</span>
                        ) : (
                          <select
                            className="admin-select"
                            value={u.role}
                            onChange={(e) => changeUserRole(u.id, e.target.value)}
                          >
                            <option value="client">Cliente</option>
                            <option value="worker">Obrero / Maestro</option>
                            <option value="supervisor">Supervisor</option>
                            <option value="admin">Administrador</option>
                          </select>
                        )}
                      </span>
                      <span data-label="Estado">
                        <span className={`badge ${u.is_active ? 'badge-success' : 'badge-warning'}`}>
                          {u.is_active ? 'Activo' : 'Inactivo'}
                        </span>
                      </span>
                      <span data-label="Acciones">
                        {u.id === user?.id ? (
                          <small>—</small>
                        ) : (
                          <div className="admin-actions">
                            <button
                              className="btn btn-outline btn-sm"
                              onClick={() => toggleUserActive(u.id, !u.is_active)}
                            >
                              {u.is_active ? 'Desactivar' : 'Activar'}
                            </button>
                            <button
                              className="btn btn-danger btn-sm"
                              onClick={() => removeUser(u.id, u.full_name || u.username)}
                              title="Eliminar usuario"
                            >
                              <DeleteIcon sx={{ fontSize: 18 }} />
                            </button>
                          </div>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* ===== CATEGORÍAS ===== */}
          {tab === 'categories' && isSuperAdmin && (
            <>
              <h1>Categorías</h1>
              <form className="cat-form" onSubmit={addCategory}>
                <div className="cat-form-fields">
                  <input
                    type="text"
                    placeholder="Nombre de la categoría *"
                    value={catForm.name}
                    onChange={(e) => setCatForm({ ...catForm, name: e.target.value })}
                    required
                  />
                  <input
                    type="text"
                    placeholder="Ícono (ej: plumbing)"
                    value={catForm.icon}
                    onChange={(e) => setCatForm({ ...catForm, icon: e.target.value })}
                  />
                  <input
                    type="text"
                    placeholder="Descripción (opcional)"
                    value={catForm.description}
                    onChange={(e) => setCatForm({ ...catForm, description: e.target.value })}
                  />
                  <button className="btn btn-primary" type="submit" disabled={catSaving}>
                    {catSaving ? 'Agregando...' : '+ Agregar'}
                  </button>
                </div>
                {catError && <div className="eval-error">{catError}</div>}
              </form>

              {loading ? (
                <div className="admin-loading"><p>Cargando...</p></div>
              ) : categories.length === 0 ? (
                <div className="admin-empty"><p>No hay categorías.</p></div>
              ) : (
                <div className="admin-table cat-table">
                  <div className="admin-table-head">
                    <span>Nombre</span>
                    <span>Ícono</span>
                    <span>Servicios</span>
                    <span>Trabajadores</span>
                    <span>Acción</span>
                  </div>
                  {categories.map((c) => (
                    <div key={c.id} className="admin-table-row">
                      <span data-label="Nombre"><strong>{c.name}</strong></span>
                      <span data-label="Ícono">{c.icon || '—'}</span>
                      <span data-label="Servicios">{c.services_count}</span>
                      <span data-label="Trabajadores">{c.workers_count}</span>
                      <span data-label="Acción">
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => removeCategory(c)}
                          title="Eliminar categoría"
                        >
                          <DeleteIcon sx={{ fontSize: 18 }} />
                        </button>
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </section>
      </div>

      {galleryImages && (
        <Lightbox images={galleryImages} index={0} onClose={() => setGalleryImages(null)} />
      )}

      {newUser && (
        <div className="eval-overlay" onClick={() => setNewUser(null)}>
          <div className="eval-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Crear usuario</h2>
            <p className="eval-sub">
              Solo el administrador puede crear supervisores o nuevos administradores.
            </p>
            {newUserError && <div className="eval-error">{newUserError}</div>}
            <div className="eval-grid">
              <label className="eval-field">
                <span>Nombres</span>
                <input value={newUser.first_name}
                  onChange={(e) => setNewUser({ ...newUser, first_name: e.target.value })} />
              </label>
              <label className="eval-field">
                <span>Apellidos</span>
                <input value={newUser.last_name}
                  onChange={(e) => setNewUser({ ...newUser, last_name: e.target.value })} />
              </label>
              <label className="eval-field">
                <span>Usuario *</span>
                <input value={newUser.username}
                  onChange={(e) => setNewUser({ ...newUser, username: e.target.value })} />
              </label>
              <label className="eval-field">
                <span>Teléfono</span>
                <input value={newUser.phone}
                  onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })} />
              </label>
              <label className="eval-field">
                <span>Correo</span>
                <input type="email" value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} />
              </label>
              <label className="eval-field">
                <span>Contraseña * (mín. 8)</span>
                <input type="password" value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} />
              </label>
              <label className="eval-field">
                <span>Rol</span>
                <select className="admin-select" value={newUser.role}
                  onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}>
                  <option value="supervisor">Supervisor</option>
                  <option value="admin">Administrador</option>
                  <option value="worker">Obrero / Maestro</option>
                  <option value="client">Cliente</option>
                </select>
              </label>
            </div>
            <div className="eval-actions">
              <button className="btn btn-outline" onClick={() => setNewUser(null)} disabled={newUserSaving}>
                Cancelar
              </button>
              <button className="btn btn-primary" onClick={submitNewUser} disabled={newUserSaving}>
                {newUserSaving ? 'Creando...' : 'Crear usuario'}
              </button>
            </div>
          </div>
        </div>
      )}

      {evalWorker && evalForm && (
        <div className="eval-overlay" onClick={() => setEvalWorker(null)}>
          <div className="eval-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Evaluar a {evalWorker.name}</h2>
            <p className="eval-sub">
              Evaluación verificada por Cuadrilla Maestra · {user?.full_name || user?.username}
            </p>
            {evalError && <div className="eval-error">{evalError}</div>}
            <div className="eval-grid">
              {EVAL_FIELDS.map(([key, label]) => (
                <label key={key} className="eval-field">
                  <span>{label}</span>
                  <select
                    value={evalForm[key]}
                    onChange={(e) => setEvalForm({ ...evalForm, [key]: e.target.value })}
                    className="admin-select"
                  >
                    {[1, 2, 3, 4, 5].map((n) => (
                      <option key={n} value={n}>{n} ★</option>
                    ))}
                  </select>
                </label>
              ))}
            </div>
            <label className="eval-field-full">
              <span>Comentario</span>
              <textarea
                value={evalForm.comment}
                onChange={(e) => setEvalForm({ ...evalForm, comment: e.target.value })}
                placeholder="Observaciones del supervisor sobre el trabajador."
                rows={3}
              />
            </label>
            <div className="eval-actions">
              <button className="btn btn-outline" onClick={() => setEvalWorker(null)} disabled={evalSaving}>
                Cancelar
              </button>
              <button className="btn btn-primary" onClick={submitEval} disabled={evalSaving}>
                {evalSaving ? 'Guardando...' : evalForm.id ? 'Actualizar evaluación' : 'Guardar evaluación'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
